<div class="flex border-b-2 border-primary">
  <div class="flex-1 p-2">
    <h1 class="mb-2 text-2xl font-bold tracking-tight text-gray-900 text-center">Domain Whitelisting</h1>
    <p class="font-normal text-gray-700 text-center">Search by location to manage its whitelisted domains</p>
  </div>

  @if (selectedLocation(); as loc) {
    <div class="flex-1 flex items-center pl-2 italic">{{ loc.name }}</div>
  }
</div>

<div class="flex h-full">
  <div class="flex-1 flex flex-col h-[600px] border-r-2 border-b-2 border-primary">
    <div class="flex mx-[-1px]">
      <input
        panInput
        color="primary"
        class="w-full !rounded-none mt-[-1px]"
        placeholder="Filter {{ locations().length }} locations..."
        [(ngModel)]="locationFilter"
        data-testid="location-filter-input"
      />
    </div>

    <div class="flex-1 overflow-hidden overflow-y-auto">
      @if (locations().length === 0 && !hasLoadedLocations()) {
        <app-pan-blank-slate> <app-pan-spinner></app-pan-spinner> &nbsp;Loading... </app-pan-blank-slate>
      } @else if (locations().length === 0 && hasLoadedLocations()) {
        <app-pan-blank-slate>You do not have access to any locations.</app-pan-blank-slate>
      } @else if (visibleLocations().length === 0 && hasLoadedLocations()) {
        <app-pan-blank-slate>No locations match your query.</app-pan-blank-slate>
      } @else {
        <cdk-virtual-scroll-viewport itemSize="50" class="h-full">
          <div
            *cdkVirtualFor="let item of visibleLocations()"
            class="h-[50px] p-3 flex flex-col justify-center border-b-2 cursor-pointer"
            [class.bg-primary-no-hover]="item === selectedLocation()"
            (click)="selectLocation(item)"
          >
            <div
              #locationName
              #locationTooltip="matTooltip"
              class="truncate w-0 min-w-full shrink-0"
              [matTooltip]="item.name"
              (mouseenter)="locationTooltip.disabled = locationName.scrollWidth <= locationName.clientWidth"
            >
              {{ item.name }}
            </div>

            @if (item.branch_name) {
              <div class="text-sm italic">
                {{ item.branch_name }}
              </div>
            }
          </div>
        </cdk-virtual-scroll-viewport>
      }
    </div>
  </div>

  <div class="flex-1 h-[600px] overflow-hidden overflow-y-auto border-r-2 border-b-2 border-primary">
    @if (!selectedLocation()) {
      <app-pan-blank-slate>Select a location to see domains for it.</app-pan-blank-slate>
    } @else if (!hasLoadedDomains()) {
      <app-pan-blank-slate> <app-pan-spinner></app-pan-spinner> &nbsp;Loading... </app-pan-blank-slate>
    } @else if (hasNoDomains()) {
      <app-pan-blank-slate>
        There are no domains assigned to this location.

        <app-pan-button icon="add" color="primary" fill="outline" (click)="addDomainModal.show()">Add Domain</app-pan-button>
      </app-pan-blank-slate>
    } @else {
      <div class="flex mx-[-1px] items-center relative">
        <input
          panInput
          color="primary"
          class="w-full !rounded-none mt-[-1px]"
          placeholder="Search domains..."
          [(ngModel)]="domainFilter"
          data-testid="domain-filter-input"
        />

        <app-pan-button class="absolute right-[10px]" size="sm" color="info" (click)="addDomainModal.show()">Add domain</app-pan-button>
      </div>
      @if (filteredDomains().length === 0) {
        <app-pan-blank-slate>No domains match your query.</app-pan-blank-slate>
      } @else {
        <cdk-virtual-scroll-viewport [itemSize]="52" class="h-[548px]">
          <div
            *cdkVirtualFor="let row of filteredDomains()"
            class="h-[52px] px-3 py-1 flex items-center border-b-2 flex-row"
          >
            <div class="flex-1 flex flex-col">
              <div>{{ row.domain }}</div>

              @if (row.inherited) {
                <div class="text-sm italic text-gray-500" [matTooltip]="row.locationGroupName ?? ''">
                  inherited from location group
                </div>
              }
            </div>

            @if (!row.inherited) {
              <app-pan-button
                color="danger"
                icon="delete"
                [swal]="removeDomainSwal"
                [attr.data-testid]="'delete-domain-button-' + row.domain"
              ></app-pan-button>

              <swal
                #removeDomainSwal
                title="Remove Whitelisted Domain?"
                text="Are you sure you want to remove this domain from {{ selectedLocation()?.name }}?"
                icon="warning"
                confirmButtonText="Yes, Remove"
                [showCancelButton]="true"
                [focusCancel]="true"
                (confirm)="removeDomain(row.domain)"
              >
              </swal>
            } @else if (userLogin.currentUserIsAdmin()) {
              <app-pan-button
                color="danger"
                icon="delete"
                [swal]="removeInheritedDomainSwal"
                [attr.data-testid]="'delete-inherited-domain-button-' + row.domain"
              ></app-pan-button>

              <swal
                #removeInheritedDomainSwal
                title="Remove Domain From Location Group?"
                text="Are you sure you want to remove this domain from {{ row.locationGroupName }}?"
                icon="warning"
                confirmButtonText="Yes, Remove"
                [showCancelButton]="true"
                [focusCancel]="true"
                (confirm)="removeInheritedDomain(row)"
              >
              </swal>
            }
          </div>
        </cdk-virtual-scroll-viewport>
      }
    }
  </div>
</div>

<app-pan-modal #addDomainModal (modalClose)="newDomain.set('')">
  <div header>Add Domain</div>

  <div body>
    <app-pan-form-control>
      <app-pan-floating-label>Enter Domain</app-pan-floating-label>
      <input type="text" [(ngModel)]="newDomain" panInput placeholder="Enter domain..." data-testid="new-domain-input" />
    </app-pan-form-control>
  </div>

  <div footer>
    <app-pan-button color="primary" [disabled]="!canSubmitModal()" (click)="submitModal()">Add Domain</app-pan-button>
  </div>
</app-pan-modal>
